import type { TankFamilyId, TankSpecial } from "../types/tanks";

/**
 * Linhagens (famílias) de tanques do Rocketanks.
 *
 * Cada família é um arquétipo de gameplay com identidade visual própria:
 * o envelope de stats orienta o orçamento de pontos do catálogo
 * (scripts/generate-tank-catalog.ts), o body kit é renderizado por
 * TankBodyKit e o pool de specials define quais poderes a linhagem recebe.
 */
export interface TankFamily {
  id: TankFamilyId;
  /** Emoji usado em badges de UI. */
  icon: string;
  /** Cor de destaque para chips/badges de UI. */
  accent: string;
  /**
   * Distribuição do orçamento de pontos entre stats (soma = 1).
   * Tanques da mesma família+tier têm o mesmo poder total, distribuído assim.
   */
  profile: { speed: number; damage: number; health: number };
  /** Poderes que a linhagem pode receber (tiers 3+). */
  specialsPool: TankSpecial[];
  /** Paleta de cores primárias da linhagem (atribuídas por slot). */
  palette: string[];
}

export const TANK_FAMILIES: Record<TankFamilyId, TankFamily> = {
  predator: {
    id: "predator",
    icon: "🐺",
    accent: "#f59e0b",
    profile: { speed: 0.42, damage: 0.33, health: 0.25 },
    specialsPool: ["homing", "piercing"],
    palette: ["#FF6B35", "#FF4500", "#DAA520", "#E9967A", "#D2691E", "#CD5C5C"],
  },
  colossus: {
    id: "colossus",
    icon: "🐻",
    accent: "#a16207",
    profile: { speed: 0.18, damage: 0.30, health: 0.52 },
    specialsPool: ["shield", "explosive"],
    palette: ["#8B4513", "#556B2F", "#8FBC8F", "#B8860B", "#A9A9A9", "#778899"],
  },
  venom: {
    id: "venom",
    icon: "🦂",
    accent: "#84cc16",
    profile: { speed: 0.35, damage: 0.40, health: 0.25 },
    specialsPool: ["piercing", "regen"],
    palette: ["#32CD32", "#00FF00", "#9ACD32", "#6B8E23", "#ADFF2F", "#98FB98"],
  },
  storm: {
    id: "storm",
    icon: "⚡",
    accent: "#38bdf8",
    profile: { speed: 0.22, damage: 0.48, health: 0.30 },
    specialsPool: ["explosive", "homing"],
    palette: ["#00CED1", "#4169E1", "#87CEEB", "#6495ED", "#40E0D0", "#B0C4DE"],
  },
  phantom: {
    id: "phantom",
    icon: "👻",
    accent: "#a78bfa",
    profile: { speed: 0.40, damage: 0.35, health: 0.25 },
    specialsPool: ["stealth", "piercing"],
    palette: ["#9400D3", "#8A2BE2", "#6A5ACD", "#7B68EE", "#9932CC", "#DDA0DD"],
  },
  bulwark: {
    id: "bulwark",
    icon: "💎",
    accent: "#22d3ee",
    profile: { speed: 0.25, damage: 0.30, health: 0.45 },
    specialsPool: ["shield", "regen"],
    palette: ["#20B2AA", "#2E8B57", "#008B8B", "#AFEEEE", "#F0E68C", "#E6E6FA"],
  },
  cosmic: {
    id: "cosmic",
    icon: "🌌",
    accent: "#818cf8",
    profile: { speed: 0.33, damage: 0.34, health: 0.33 },
    specialsPool: ["homing", "shield"],
    palette: ["#00FFFF", "#00008B", "#4B0082", "#7DF9FF", "#8470FF", "#87CEFA"],
  },
  mythic: {
    id: "mythic",
    icon: "🐲",
    accent: "#facc15",
    profile: { speed: 0.30, damage: 0.40, health: 0.30 },
    specialsPool: ["explosive", "homing", "piercing", "shield"],
    palette: ["#FFD700", "#DC143C", "#FF1493", "#FF69B4", "#EE82EE", "#FFB6C1"],
  },
};

export const TANK_FAMILY_IDS = Object.keys(TANK_FAMILIES) as TankFamilyId[];

export function getTankFamily(id: TankFamilyId | undefined): TankFamily | null {
  return id ? TANK_FAMILIES[id] ?? null : null;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Chave i18n do nome da família (ex.: "tank.familyPredator"). */
export function familyNameKey(id: TankFamilyId): string {
  return `tank.family${capitalize(id)}`;
}

/** Chave i18n da descrição da família (ex.: "tank.familyDescPredator"). */
export function familyDescKey(id: TankFamilyId): string {
  return `tank.familyDesc${capitalize(id)}`;
}

/** Chave i18n do nome do special (ex.: "tank.specialShield"). */
export function specialNameKey(id: TankSpecial): string {
  return `tank.special${capitalize(id)}`;
}

/** Chave i18n da descrição do special (ex.: "tank.specialDescShield"). */
export function specialDescKey(id: TankSpecial): string {
  return `tank.specialDesc${capitalize(id)}`;
}
